import {
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';

import {
  AutocompleteFilter,
  AutocompleteOption,
  DateFilter,
  DateRangeConfig,
  Filter,
  MultipleFilter,
  SelectFilter,
  SelectOption,
  TextFilter,
} from './filterTypes';

const BUSINESS_PRELOAD_FIELDS = [
  'stakeholderName',
  'businessName',
  'businessOriginName',
  'businessDestinationName',
];

export const createDateFilter = (
  dateKey: string,
  label: string,
  value: string | null | undefined,
  setter: (value: string | null) => void,
  dateType?: string,
  dateLabel?: string,
  pairKey?: string,
): DateFilter => ({
  type: 'date',
  label,
  dateKey,
  value: value || null,
  setter,
  dateType,
  dateLabel,
  pairKey,
});

export const createDateRangeFilters = (
  config: DateRangeConfig,
): [DateFilter, DateFilter] => {
  return [
    createDateFilter(
      config.startDateKey,
      config.label,
      config.startValue,
      config.startSetter,
      config.dateType,
      config.dateLabel || config.label,
      config.endDateKey,
    ),
    createDateFilter(
      config.endDateKey,
      config.label,
      config.endValue,
      config.endSetter,
      config.dateType,
      config.dateLabel || config.label,
      config.startDateKey,
    ),
  ];
};

export const createTextFilter = (
  label: string,
  value: string | undefined,
  setter: (value: string) => void,
  specialHandler?: 'id' | string,
  key?: string,
): TextFilter => ({
  type: 'text',
  label,
  value: value || '',
  setter,
  specialHandler,
  key,
});

export const createSelectFilter = (
  label: string,
  options: SelectOption[],
  value: string | undefined,
  setter: (value: string) => void,
  key?: string,
): SelectFilter => ({
  type: 'select',
  label,
  options,
  value: value || '',
  setter,
  key,
});

export const createMultipleFilter = (
  label: string,
  options: SelectOption[],
  value: SelectOption[] | undefined,
  setter: (value: SelectOption[]) => void,
  key?: string,
): MultipleFilter => ({
  type: 'multiple',
  label,
  options,
  value: value || [],
  setter,
  key,
});

export const createAutocompleteFilter = (
  label: string,
  key: string,
  value: string | null | undefined,
  setter: (value: string | null) => void,
  selectedValue?: AutocompleteOption | null,
  setSelectedValue?: (value: AutocompleteOption | null) => void,
): AutocompleteFilter => ({
  type: 'autocomplete',
  label,
  key,
  value: value || null,
  setter,
  selectedValue,
  setSelectedValue,
  usePreloadedData: BUSINESS_PRELOAD_FIELDS.includes(key),
});

export const isDateFilter = (filter: Filter): filter is DateFilter => {
  return filter.type === 'date';
};

export const isTextFilter = (filter: Filter): filter is TextFilter => {
  return filter.type === 'text';
};

export const isSelectFilter = (filter: Filter): filter is SelectFilter => {
  return filter.type === 'select';
};

export const isMultipleFilter = (filter: Filter): filter is MultipleFilter => {
  return filter.type === 'multiple';
};

export const isAutocompleteFilter = (
  filter: Filter,
): filter is AutocompleteFilter => {
  return filter.type === 'autocomplete';
};

export interface DatePair {
  startFilter: DateFilter;
  endFilter: DateFilter;
  label: string;
}

export const groupDateFilters = (filters: Filter[]): DatePair[] => {
  const dateGroups: DatePair[] = [];
  const processedKeys = new Set<string>();

  filters.forEach((filter) => {
    if (
      isDateFilter(filter) &&
      filter.dateKey &&
      !processedKeys.has(filter.dateKey)
    ) {
      const isStartDate = filter.dateKey.startsWith('start');

      if (isStartDate) {
        let endFilter: DateFilter | undefined;

        // Si tiene pairKey explícito, usarlo
        if (filter.pairKey) {
          endFilter = filters.find(
            (f) => isDateFilter(f) && f.dateKey === filter.pairKey,
          ) as DateFilter | undefined;
        } else {
          // Fallback: detectar automáticamente usando el patrón start/end
          const baseKey = filter.dateKey.replace('start', '');
          const endKey = `end${baseKey}`;
          endFilter = filters.find(
            (f) => isDateFilter(f) && f.dateKey === endKey,
          ) as DateFilter | undefined;
        }

        if (endFilter) {
          dateGroups.push({
            startFilter: filter,
            endFilter: endFilter,
            label: filter.label,
          });
          processedKeys.add(filter.dateKey);
          if (endFilter.dateKey) {
            processedKeys.add(endFilter.dateKey);
          }
        }
      }
    }
  });

  return dateGroups;
};

export const getNonDateFilters = (filters: Filter[]): Filter[] => {
  return filters.filter((filter) => !isDateFilter(filter));
};

interface TextFilterRendererProps {
  filter: TextFilter;
  value: string;
  onChange: (value: string) => void;
}

export const renderTextFilter = ({
  filter,
  value,
  onChange,
}: TextFilterRendererProps) => (
  <TextField
    label={filter.label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    variant="outlined"
    size="small"
  />
);

interface SelectFilterRendererProps {
  filter: SelectFilter;
  onChange: (value: string) => void;
}

export const renderSelectFilter = ({
  filter,
  onChange,
}: SelectFilterRendererProps) => (
  <FormControl fullWidth size="small">
    <InputLabel>{filter.label}</InputLabel>
    <Select
      value={filter.value || ''}
      onChange={(e) => onChange(e.target.value)}
      label={filter.label}
    >
      {filter.options?.map((option, idx) => (
        <MenuItem key={idx} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

interface MultipleFilterRendererProps {
  filter: MultipleFilter;
  onChange: (value: SelectOption[]) => void;
}

export const renderMultipleFilter = ({
  filter,
  onChange,
}: MultipleFilterRendererProps) => {
  const options = filter.options || [];
  return (
    <Autocomplete
      multiple
      value={filter.value || []}
      onChange={(e, value) => onChange(value)}
      options={options}
      renderInput={(params) => (
        <TextField {...params} label={filter.label} size="small" />
      )}
    />
  );
};

interface AutocompleteFilterRendererProps {
  filter: AutocompleteFilter;
  inputValue: string;
  onInputChange: (value: string, reason: string) => void;
  onChange: (value: AutocompleteOption | null) => void;
  options: AutocompleteOption[];
  loading: boolean;
  value: AutocompleteOption | null;
}

export const renderAutocompleteFilter = ({
  filter,
  inputValue,
  onInputChange,
  onChange,
  options,
  loading,
  value,
}: AutocompleteFilterRendererProps) => (
  <Autocomplete
    value={value}
    onChange={(e, newValue) => onChange(newValue)}
    inputValue={inputValue}
    onInputChange={(e, newInputValue, reason) =>
      onInputChange(newInputValue, reason)
    }
    options={options}
    loading={loading}
    loadingText="Cargando opciones..."
    getOptionLabel={(option) =>
      typeof option === 'string' ? option : option.label
    }
    isOptionEqualToValue={(option, val) => option?.value === val?.value}
    renderInput={(params) => (
      <TextField
        {...params}
        label={filter.label}
        size="small"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? (
                <div>
                  <CircularProgress size={20} />
                </div>
              ) : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  />
);
